import type { PrescriptionPrintTemplateId } from '@helios/data-access';

/**
 * System print templates available in v1 (PRD.184 §6.1).
 *
 * Each template defines high-level layout strategy hints that the
 * generated `PrescriptionPrintLayout` renderer applies on top of the
 * clinic's branding + section settings.
 */

export type HeaderAlign = 'left' | 'center' | 'right';
export type Density = 'compact' | 'comfortable';
export type DoctorBlockPosition = 'header_right' | 'header_below_clinic' | 'footer';
export type ItemsLayout = 'table' | 'numbered_list';

/**
 * PRD.320 — which optional print controls a template supports.
 *
 * Capability-driven so the wizard never asks "is this dental_structured?".
 * A control renders when the SELECTED template declares it, existing templates
 * declare nothing and are therefore untouched, and a future template opts in
 * by adding a flag rather than by editing the wizard.
 *
 * Absent === unsupported, so this stays fully optional.
 */
export interface TemplateCapabilities {
  logoSize?: boolean;
  headerSize?: boolean;
  vitalsLayout?: boolean;
  /** The Clinical Note block and its per-section toggles. */
  clinicalNotes?: boolean;
  /** Per-section `lines` / `comma` list rendering. */
  clinicalSectionLayout?: boolean;
  /** Overall + per-section type scale. */
  typography?: boolean;
}

export interface SystemTemplate {
  id: PrescriptionPrintTemplateId;
  label: string;
  description: string;

  headerAlign: HeaderAlign;
  showRxSymbol: boolean;
  doctorBlock: DoctorBlockPosition;
  itemsLayout: ItemsLayout;
  density: Density;
  /** Whether the template emphasizes the logo (larger logo treatment in header). */
  prominentLogo: boolean;
  /** PRD.320 — optional controls this template opts into. Absent = none. */
  capabilities?: TemplateCapabilities;
}

export const SYSTEM_TEMPLATES: SystemTemplate[] = [
  {
    id: 'classic_clinical',
    label: 'Classic Clinical',
    description:
      'Polished letterhead — clinic on the left, doctor credentials on the right, themed items table with M-A-N dosage cells, signature block, and an iconified 3-column footer.',
    headerAlign: 'left',
    showRxSymbol: false,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    id: 'clean_classic',
    label: 'Clean Classic',
    description: 'Centered logo + clinic header with the classic Rx symbol and a tidy items table.',
    headerAlign: 'center',
    showRxSymbol: true,
    doctorBlock: 'header_below_clinic',
    itemsLayout: 'table',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    id: 'compact_modern',
    label: 'Compact Modern',
    description: 'Left-aligned modern header. Dense items table — fits more rows per page.',
    headerAlign: 'left',
    showRxSymbol: false,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'compact',
    prominentLogo: false,
  },
  {
    id: 'doctor_letterhead',
    label: 'Doctor Letterhead',
    description:
      'Letterhead-style header with clinic on the left and doctor credentials on the right.',
    headerAlign: 'left',
    showRxSymbol: true,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    id: 'minimal_prescription',
    label: 'Minimal Prescription',
    description: 'Stripped-back layout — no Rx symbol, simple numbered list of prescribed items.',
    headerAlign: 'left',
    showRxSymbol: false,
    doctorBlock: 'footer',
    itemsLayout: 'numbered_list',
    density: 'compact',
    prominentLogo: false,
  },
  {
    // PRD.284 — Letterhead Classic. Renders via a dedicated layout branch in
    // PrescriptionPrintLayout (two-column doctor/practice header, large writing
    // area, centered footer) for clinics using traditional pre-printed pads.
    id: 'classic_letterhead',
    label: 'Letterhead Classic',
    description:
      'Traditional two-column doctor header with centered footer for pre-printed clinic stationery.',
    headerAlign: 'left',
    showRxSymbol: true,
    doctorBlock: 'header_right',
    itemsLayout: 'numbered_list',
    density: 'comfortable',
    prominentLogo: true,
  },
  {
    // PRD.320 — Dental Structured. A clinical encounter document with the Rx
    // below it: clinic letterhead, two-column patient/visit block, vitals
    // strip, a Clinical Note composed from canonical encounter domains, then
    // the medication table. Rendered by its own branch in
    // PrescriptionPrintLayout.
    id: 'dental_structured',
    label: 'Dental Structured',
    description:
      'Clinical encounter document with vitals, Visit Note details and the prescription below.',
    headerAlign: 'left',
    showRxSymbol: true,
    doctorBlock: 'header_right',
    itemsLayout: 'table',
    density: 'compact',
    prominentLogo: true,
    // The only template that opts into the PRD.320 controls. Every other
    // template declares none and renders exactly as before.
    capabilities: {
      logoSize: true,
      headerSize: true,
      vitalsLayout: true,
      clinicalNotes: true,
      clinicalSectionLayout: true,
      typography: true,
    },
  },
];

/** Whether the selected template supports a given optional control. */
export function templateSupports(
  id: PrescriptionPrintTemplateId,
  capability: keyof TemplateCapabilities,
): boolean {
  return getSystemTemplate(id).capabilities?.[capability] === true;
}

export function getSystemTemplate(id: PrescriptionPrintTemplateId): SystemTemplate {
  return SYSTEM_TEMPLATES.find((t) => t.id === id) ?? SYSTEM_TEMPLATES[0];
}
